import React, { useState, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
  Grid,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  Box,
  TextField,
  IconButton,
  Chip,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import GroupIcon from '@mui/icons-material/Group';
import PhoneIcon from '@mui/icons-material/Phone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import fetchJSON from '../utils/fetchJSON';
import { toast } from 'react-toastify';
import { usePermissions } from '../utils/usePermissions';
import io from 'socket.io-client';

// Custom theme for a consistent look
const theme = createTheme({
  palette: {
    primary: {
      main: '#1674BB',
    },
    success: {
      main: '#28a745',
    },
    warning: {
      main: '#ffc107',
    },
    error: {
      main: '#dc3545',
    },
  },
  typography: {
    fontFamily: 'Roboto, Arial, sans-serif',
  },
});

const HEALTH_PROVIDER_ROLES = ['doctor', 'nurse', 'physiotherapist', 'social worker', 'pharmacist'];
const API_FILES_BASE = 'https://apihealthconnect.kopanovertex.com/images/';

/** Portal JWT — sensitive /images/* (ID docs, certificates) require this. */
const getAccessToken = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    return token.startsWith('Bearer ') ? token.slice(7).trim() : token.trim();
  } catch {
    return null;
  }
};

/** Authenticated file URL (?access_token=) for <img>/<a> — backend SENSITIVE_STATIC_RE. */
const getFileUrl = (filename) => {
  if (!filename) return '';
  const base = `${API_FILES_BASE}${filename}`;
  const token = getAccessToken();
  if (!token) return base;
  return `${base}?access_token=${encodeURIComponent(token)}`;
};

const isPdfFile = (filename) => /\.pdf$/i.test(filename || '');

const emptyDocumentBoxSx = {
  width: '100%',
  height: 200,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 2,
  border: '1px dashed #e0e0e0',
  backgroundColor: '#fafafa',
};

const framedDocumentSx = {
  width: '100%',
  maxHeight: 400,
  objectFit: 'contain',
  borderRadius: 2,
  border: '1px solid #e0e0e0',
  boxShadow: 1,
  backgroundColor: '#f5f5f5',
  p: 1,
};

function UploadedFilePreview({ filename, label, asPdf = false }) {
  const url = getFileUrl(filename);
  const showAsPdf = Boolean(filename) && (asPdf || isPdfFile(filename));
  const [blobUrl, setBlobUrl] = useState(null);
  const [loadError, setLoadError] = useState(false);

  // Fetch with Authorization so ID docs / certs pass the sensitive-static JWT gate.
  // PDFs also become same-origin blobs (avoids X-Frame-Options blocking the iframe).
  useEffect(() => {
    if (!filename || !url) {
      setBlobUrl(null);
      setLoadError(false);
      return undefined;
    }

    let cancelled = false;
    let objectUrl;

    (async () => {
      try {
        const token = getAccessToken();
        const headers = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
          headers['x-access-token'] = `Bearer ${token}`;
        }
        const response = await fetch(url, { mode: 'cors', headers });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) {
          setBlobUrl(objectUrl);
          setLoadError(false);
        } else {
          URL.revokeObjectURL(objectUrl);
        }
      } catch {
        if (!cancelled) {
          setBlobUrl(null);
          setLoadError(true);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [filename, url]);

  if (!filename) {
    return (
      <Box sx={emptyDocumentBoxSx}>
        <Typography variant="body2" color="text.secondary">Not provided</Typography>
      </Box>
    );
  }

  if (!blobUrl && !loadError) {
    return (
      <Box sx={emptyDocumentBoxSx}>
        <Typography variant="body2" color="text.secondary">Loading…</Typography>
      </Box>
    );
  }

  if (showAsPdf) {
    return (
      <Stack spacing={1}>
        <Box
          sx={{
            width: '100%',
            height: 400,
            borderRadius: 2,
            border: '1px solid #e0e0e0',
            boxShadow: 1,
            overflow: 'hidden',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {blobUrl && !loadError ? (
            <Box
              component="iframe"
              src={blobUrl}
              title={label}
              sx={{ width: '100%', height: '100%', border: 'none' }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, textAlign: 'center' }}>
              Inline preview unavailable. Use Open PDF in new tab.
            </Typography>
          )}
        </Box>
        <Button
          component="a"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          variant="outlined"
          startIcon={<PictureAsPdfIcon />}
          endIcon={<OpenInNewIcon />}
        >
          Open PDF in new tab
        </Button>
      </Stack>
    );
  }

  if (loadError) {
    return (
      <Box sx={emptyDocumentBoxSx}>
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, textAlign: 'center' }}>
          Unable to load document. Sign in again if this persists.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={blobUrl}
      alt={label}
      sx={framedDocumentSx}
    />
  );
}

export default function UserManagement() {
  const { canRead, canWrite, canDelete } = usePermissions();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    id: null,
    fullname: '',
    cellphoneNumber: '',
    address: '',
    role: 'passager',
    driverIDFront: '',
    driverIDback: '',
    licenseFront: '',
    licenseBack: '',
    frontSeats: '',
    licensePlate: '',
    rearSeats: '',
    badge: '',
    drivingZone: '',
    profileImage: '',
    verifiedCellphoneNumber: '',
    carModel: '',
    visibility: 'Offline',
    accountDeactivation: false,
    isDocumentVerified: false,
    isDocumentsSubmitted: false,
    isDriverProfileImageVerified: false,
    isAccountVerified: false,
    dateProfileImageUpdated: null,
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [selectedUserForRejection, setSelectedUserForRejection] = useState(null);
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [selectedUserForDocuments, setSelectedUserForDocuments] = useState(null);

  // Fetch users from backend
  useEffect(() => {
    fetchUsers();
  }, []);

  // Live Online/Offline status from socket presence (userId in userSockets → Online)
  useEffect(() => {
    const applyPresence = (onlineUserIds) => {
      const onlineSet = new Set((onlineUserIds || []).map(String));
      setUsers((prev) =>
        prev.map((user) => {
          const userId = String(user._id || user.id);
          const visibility = onlineSet.has(userId) ? 'Online' : 'Offline';
          return user.visibility === visibility ? user : { ...user, visibility };
        })
      );
    };

    const newSocket = io('https://apihealthconnect.kopanovertex.com', {
      transports: ['websocket', 'polling'],
    });

    newSocket.on('onlineUsersUpdate', (data) => {
      applyPresence(data.onlineUserIds);
    });

    return () => {
      newSocket.disconnect();
    };
  }, []);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response = await fetchJSON('https://apihealthconnect.kopanovertex.com/api/app/auth/all-users');
      if (response.status && response.users) {
        // Normalize user data - map _id to id for DataGrid
        const normalizedUsers = response.users.map(user => ({
          ...user,
          id: user._id || user.id,
        }));
        // Debug: Check if region and profileImage are present
        if (normalizedUsers.length > 0) {
          console.log('Sample user data:', {
            fullname: normalizedUsers[0].fullname,
            region: normalizedUsers[0].region,
            profileImage: normalizedUsers[0].profileImage
          });
        }
        setUsers(normalizedUsers);
        setFilteredUsers(normalizedUsers);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(error.message || 'Failed to fetch users');
    } finally {
      setIsLoading(false);
    }
  };

  // Update filteredUsers based on search and filters
  useEffect(() => {
    let filtered = users;

    // Apply search filter
    if (searchQuery) {
      const lowercasedQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.fullname?.toLowerCase().includes(lowercasedQuery) ||
          user.cellphoneNumber.toLowerCase().includes(lowercasedQuery) ||
          user.address?.toLowerCase().includes(lowercasedQuery) ||
          user.role.toLowerCase().includes(lowercasedQuery)
      );
    }

    // Apply role filter
    if (filterRole !== 'all') {
      filtered = filtered.filter((user) => user.role === filterRole);
    }

    setFilteredUsers(filtered);
  }, [users, searchQuery, filterRole]);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setIsEdit(true);
      setCurrentUser(user);
    } else {
      setIsEdit(false);
      setCurrentUser({
        id: null,
        fullname: '',
        cellphoneNumber: '',
        address: '',
        role: 'passager',
        driverIDFront: '',
        driverIDback: '',
        licenseFront: '',
        licenseBack: '',
        frontSeats: '',
        licensePlate: '',
        rearSeats: '',
        badge: '',
        drivingZone: '',
        profileImage: '',
        verifiedCellphoneNumber: '',
        carModel: '',
        visibility: 'Offline',
        accountDeactivation: false,
        isDocumentVerified: false,
        isDocumentsSubmitted: false,
        isDriverProfileImageVerified: false,
        isAccountVerified: false,
        dateProfileImageUpdated: null,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentUser((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = () => {
    if (!currentUser.cellphoneNumber || !currentUser.verifiedCellphoneNumber) {
      setSnackbarMessage('Cellphone Number and Verified Cellphone Number are required.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (isEdit) {
      setUsers((prev) =>
        prev.map((user) => (user.id === currentUser.id ? currentUser : user))
      );
      setSnackbarMessage('User updated successfully!');
    } else {
      const newUser = {
        ...currentUser,
        id: users.length > 0 ? Math.max(...users.map((u) => u.id)) + 1 : 1,
      };
      setUsers((prev) => [...prev, newUser]);
      setSnackbarMessage('User added successfully!');
    }
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
    handleCloseDialog();
  };

  const handleDelete = (id) => {
    setUsers((prev) => prev.filter((user) => user.id !== id));
    setSnackbarMessage('User deleted successfully!');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  const handleApproveDocuments = async (userId) => {
    try {
      setIsLoading(true);
      const response = await fetchJSON(
        `https://apihealthconnect.kopanovertex.com/api/app/auth/approve-documents/${userId}`,
        'PATCH'
      );
      if (response.status) {
        toast.success('Documents approved successfully!');
        await fetchUsers(); // Refresh users list
      }
    } catch (error) {
      console.error('Error approving documents:', error);
      toast.error(error.message || 'Failed to approve documents');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectDocuments = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      setIsLoading(true);
      const response = await fetchJSON(
        `https://apihealthconnect.kopanovertex.com/api/app/auth/reject-documents/${selectedUserForRejection}`,
        'PATCH',
        { reason: rejectionReason }
      );
      if (response.status) {
        toast.success('Documents rejected. Notification sent to user.');
        setRejectDialogOpen(false);
        setRejectionReason('');
        setSelectedUserForRejection(null);
        await fetchUsers(); // Refresh users list
      }
    } catch (error) {
      console.error('Error rejecting documents:', error);
      toast.error(error.message || 'Failed to reject documents');
    } finally {
      setIsLoading(false);
    }
  };

  const openRejectDialog = (userId) => {
    setSelectedUserForRejection(userId);
    setRejectDialogOpen(true);
  };

  const openDocumentsDialog = (user) => {
    console.log('Opening documents dialog for user:', {
      fullname: user.fullname,
      region: user.region,
      profileImage: user.profileImage,
      allFields: user
    });
    setSelectedUserForDocuments(user);
    setDocumentsDialogOpen(true);
  };

  const isHealthProvider = (role) => HEALTH_PROVIDER_ROLES.includes(role);

  const isPharmacist = (role) => role === 'pharmacist';

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'patient':
        return <AccountCircleIcon />;
      case 'doctor':
      case 'nurse':
      case 'physiotherapist':
      case 'social worker':
        return <LocalHospitalIcon />;
      case 'pharmacist':
        return <LocalPharmacyIcon />;
      default:
        return <AccountCircleIcon />;
    }
  };

  // Column definitions for the DataGrid
  const userColumns = [
    {
      field: 'profileImage',
      headerName: 'Avatar',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Avatar 
          src={params.row.profileImage ? `https://apihealthconnect.kopanovertex.com/images/${params.row.profileImage}` : undefined}
          alt={params.row.fullname}
        >
          {params.row.fullname ? params.row.fullname.charAt(0).toUpperCase() : 'U'}
        </Avatar>
      ),
    },
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'fullname', headerName: 'Full Name', width: 130 },
    { field: 'cellphoneNumber', headerName: 'Phone', width: 120 },
    {
      field: 'role',
      headerName: 'Role',
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === 'driver' ? 'primary' : 'default'}
          size="small"
          icon={getRoleIcon(params.value)}
        />
      ),
    },
    {
      field: 'visibility',
      headerName: 'Status',
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === 'Online' ? 'success' : 'error'}
          size="small"
        />
      ),
    },
    {
      field: 'isAccountVerified',
      headerName: 'Verified',
      width: 100,
      renderCell: (params) => (
        params.value ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />
      ),
    },
    {
      field: 'isDocumentVerified',
      headerName: 'Doc Status',
      width: 100,
      renderCell: (params) => {
        // Show document status for both patients and health providers
        const hasDocuments = params.row.role === 'patient' 
          ? (params.row.idDocumentFront && params.row.idDocumentBack && params.row.profileImage)
          : params.row.isDocumentsSubmitted;
        
        if (!hasDocuments) {
          return <Typography variant="body2" color="text.secondary">-</Typography>;
        }
        return params.value ? (
          <Chip label="Verified" color="success" size="small" icon={<CheckCircleIcon />} />
        ) : (
          <Chip label="Pending" color="warning" size="small" />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 60,
      sortable: false,
      renderCell: (params) => {
        const isPatient = params.row.role === 'patient';
        const hasPatientDocuments = isPatient && params.row.idDocumentFront && params.row.idDocumentBack && params.row.profileImage;
        const hasHealthProviderDocuments = isHealthProvider(params.row.role) && params.row.isDocumentsSubmitted;
        const hasPendingVerification = hasPatientDocuments && !params.row.isDocumentVerified;
        const hasHealthProviderPending = hasHealthProviderDocuments && !params.row.isDocumentVerified;
        const isVerified = params.row.isDocumentVerified;

        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {/* Show approve/reject button for patients with pending documents */}
            {hasPendingVerification && canRead && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openDocumentsDialog(params.row);
                }}
                title={canWrite ? "View Documents & Approve/Reject" : "View Documents"}
                color="info"
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            )}
            {/* Show view button for verified patients - can also reject */}
            {isPatient && hasPatientDocuments && isVerified && canRead && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openDocumentsDialog(params.row);
                }}
                title={canWrite ? "View Documents & Reject Verification" : "View Documents"}
                color="info"
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            )}
            {/* Show approve/reject button for health providers with pending documents */}
            {hasHealthProviderPending && canRead && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openDocumentsDialog(params.row);
                }}
                title={canWrite ? "View Documents & Approve/Reject" : "View Documents"}
                color="info"
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            )}
            {/* Show view button for verified health providers - can also reject */}
            {hasHealthProviderDocuments && isVerified && canRead && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openDocumentsDialog(params.row);
                }}
                title={canWrite ? "View Documents & Reject Verification" : "View Documents"}
                color="info"
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            )}
            {/* View Details button for users without documents or pending verification */}
            {(!hasPatientDocuments && !hasHealthProviderDocuments) && canRead && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openDocumentsDialog(params.row);
                }}
                title="View User Details"
                color="info"
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      },
    },
  ];

  // Statistics calculation
  const stats = {
    total: users.length,
    patients: users.filter((user) => user.role === 'patient').length,
    healthProviders: users.filter((user) => isHealthProvider(user.role)).length,
    pendingVerification: users.filter((user) => {
      const isPatient = user.role === 'patient';
      const hasPatientDocuments = isPatient && user.idDocumentFront && user.idDocumentBack && user.profileImage;
      const hasHealthProviderDocuments = isHealthProvider(user.role) && user.isDocumentsSubmitted;
      return (hasPatientDocuments || hasHealthProviderDocuments) && !user.isDocumentVerified;
    }).length,
  };

  return (
    <ThemeProvider theme={theme}>
      <Grid container spacing={4} sx={{ p: { xs: 2, md: 4 } }}>
        <Grid item xs={12}>
          <Typography gutterBottom>
            User Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage all registered app users, including passengers, drivers, and fleets.
          </Typography>
        </Grid>

        {/* Statistics Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <Card raised sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <AccountCircleIcon color="primary" fontSize="large" />
                <Box>
                  <Typography variant="h4" component="div">
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Users
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card raised sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <AccountCircleIcon color="action" fontSize="large" />
                <Box>
                  <Typography variant="h4" component="div">
                    {stats.patients}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Patients
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card raised sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <LocalHospitalIcon color="primary" fontSize="large" />
                <Box>
                  <Typography variant="h4" component="div">
                    {stats.healthProviders}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Health Providers
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card raised sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <GroupIcon color="warning" fontSize="large" />
                <Box>
                  <Typography variant="h4" component="div">
                    {stats.pendingVerification}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Verification
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Users Table */}
        <Grid item xs={12}>
          <Card raised>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5">
                  All Users
                </Typography>
              </Stack>

              {/* Search and Filters */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Search users"
                    variant="outlined"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <IconButton sx={{ pr: 1, color: 'text.secondary' }}>
                          <SearchIcon />
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={filterRole}
                      label="Role"
                      onChange={(e) => setFilterRole(e.target.value)}
                    >
                      <MenuItem value="all">All Roles</MenuItem>
                      <MenuItem value="patient">Patient</MenuItem>
                      <MenuItem value="doctor">Doctor</MenuItem>
                      <MenuItem value="nurse">Nurse</MenuItem>
                      <MenuItem value="physiotherapist">Physiotherapist</MenuItem>
                      <MenuItem value="social worker">Social Worker</MenuItem>
                      <MenuItem value="pharmacist">Pharmacist</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box sx={{ height: 600, width: '100%', overflowX: 'auto' }}>
                <DataGrid
                  rows={filteredUsers}
                  columns={userColumns}
                  getRowId={(row) => row.id || row._id}
                  loading={isLoading}
                  sx={{
                    '& .MuiDataGrid-root': {
                      fontFamily: 'Roboto, sans-serif',
                    },
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: 'rgba(22, 116, 187, 0.04)',
                    },
                  }}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 25,
                      },
                    },
                  }}
                  pageSizeOptions={[25, 50, 100]}
                  disableRowSelectionOnClick
                  autoHeight={false}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add/Edit User Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{isEdit ? 'Edit User' : 'Add New User'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="h6">General Information</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="fullname"
                label="Full Name"
                variant="outlined"
                fullWidth
                value={currentUser.fullname || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="cellphoneNumber"
                label="Cellphone Number"
                variant="outlined"
                fullWidth
                value={currentUser.cellphoneNumber || ''}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="address"
                label="Address"
                variant="outlined"
                fullWidth
                value={currentUser.address || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  name="role"
                  value={currentUser.role}
                  label="Role"
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="patient">Patient</MenuItem>
                  <MenuItem value="doctor">Doctor</MenuItem>
                  <MenuItem value="nurse">Nurse</MenuItem>
                  <MenuItem value="physiotherapist">Physiotherapist</MenuItem>
                  <MenuItem value="social worker">Social Worker</MenuItem>
                  <MenuItem value="pharmacist">Pharmacist</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="verifiedCellphoneNumber"
                label="Verified Cellphone Number"
                variant="outlined"
                fullWidth
                value={currentUser.verifiedCellphoneNumber || ''}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Visibility</InputLabel>
                <Select
                  name="visibility"
                  value={currentUser.visibility || 'Offline'}
                  label="Visibility"
                  disabled
                >
                  <MenuItem value="Online">Online</MenuItem>
                  <MenuItem value="Offline">Offline</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="isAccountVerified"
                      checked={currentUser.isAccountVerified}
                      onChange={handleChange}
                    />
                  }
                  label="Account Verified"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="accountDeactivation"
                      checked={currentUser.accountDeactivation}
                      onChange={handleChange}
                    />
                  }
                  label="Account Deactivated"
                />
              </FormGroup>
            </Grid>
            
            {isHealthProvider(currentUser.role) && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6">Health Provider Details</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="hpcnaNumber"
                    label="HPCNA Number"
                    variant="outlined"
                    fullWidth
                    value={currentUser.hpcnaNumber || ''}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="operationalZone"
                    label="Operational Zone"
                    variant="outlined"
                    fullWidth
                    value={currentUser.operationalZone || ''}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormGroup row>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="isDocumentVerified"
                          checked={currentUser.isDocumentVerified}
                          onChange={handleChange}
                          disabled
                        />
                      }
                      label="Documents Verified"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="isDocumentsSubmitted"
                          checked={currentUser.isDocumentsSubmitted}
                          onChange={handleChange}
                          disabled
                        />
                      }
                      label="Documents Submitted"
                    />
                  </FormGroup>
                </Grid>

                {/* Displaying images for editing */}
                {isEdit && (
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2 }}>Submitted Documents</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="body2" color="text.secondary">ID Document Front</Typography>
                        <UploadedFilePreview
                          filename={currentUser.idDocumentFront}
                          label="ID Document Front"
                        />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="body2" color="text.secondary">ID Document Back</Typography>
                        <UploadedFilePreview
                          filename={currentUser.idDocumentBack}
                          label="ID Document Back"
                        />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="body2" color="text.secondary">Primary Qualification</Typography>
                        <UploadedFilePreview
                          filename={currentUser.finalQualification || currentUser.primaryQualification}
                          label="Primary Qualification"
                          asPdf
                        />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="body2" color="text.secondary">HPCNA Certificate</Typography>
                        <UploadedFilePreview
                          filename={
                            currentUser.hpcnaCertificate ||
                            currentUser.HPCNAQualification ||
                            currentUser.annualQualification
                          }
                          label="HPCNA Certificate"
                          asPdf
                        />
                      </Grid>
                      {(currentUser.dispensingCertificateLicence) && (
                        <Grid item xs={6} sm={4}>
                          <Typography variant="body2" color="text.secondary">Dispensing Certificate / Licence</Typography>
                          <UploadedFilePreview
                            filename={currentUser.dispensingCertificateLicence}
                            label="Dispensing Certificate / Licence"
                            asPdf
                          />
                        </Grid>
                      )}
                      <Grid item xs={6} sm={4}>
                        <Typography variant="body2" color="text.secondary">Profile Image</Typography>
                        {currentUser.profileImage && (
                          <img src={`https://apihealthconnect.kopanovertex.com/images/${currentUser.profileImage}`} alt="Profile Image" style={{ width: '100%', height: 'auto', borderRadius: 8 }} />
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            startIcon={isEdit ? <EditIcon /> : <PersonAddIcon />}
          >
            {isEdit ? 'Update User' : 'Add User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Documents Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => {
          setRejectDialogOpen(false);
          setRejectionReason('');
          setSelectedUserForRejection(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Documents</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please provide a reason for rejecting the documents. This reason will be sent to the user via notification.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Enter the reason for rejection..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRejectDialogOpen(false);
              setRejectionReason('');
              setSelectedUserForRejection(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectDocuments}
            disabled={!rejectionReason.trim()}
          >
            Reject Documents
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Documents Dialog */}
      <Dialog
        open={documentsDialogOpen}
        onClose={() => {
          setDocumentsDialogOpen(false);
          setSelectedUserForDocuments(null);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          User Registration - {selectedUserForDocuments?.fullname || 'User'}
        </DialogTitle>
        <DialogContent>
          {selectedUserForDocuments && (
            <>
              {/* User Information - Readonly */}
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>User Information</Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                {/* Profile Image */}
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>Profile Image</Typography>
                  {selectedUserForDocuments.profileImage ? (
                    <Avatar
                      src={`https://apihealthconnect.kopanovertex.com/images/${selectedUserForDocuments.profileImage}`}
                      alt={selectedUserForDocuments.fullname || 'User'}
                      sx={{ width: 120, height: 120, mb: 2 }}
                    >
                      {selectedUserForDocuments.fullname ? selectedUserForDocuments.fullname.charAt(0).toUpperCase() : 'U'}
                    </Avatar>
                  ) : (
                    <Avatar sx={{ width: 120, height: 120, mb: 2 }}>
                      {selectedUserForDocuments.fullname ? selectedUserForDocuments.fullname.charAt(0).toUpperCase() : 'U'}
                    </Avatar>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Full Name"
                    value={selectedUserForDocuments.fullname || ''}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Cellphone Number"
                    value={selectedUserForDocuments.cellphoneNumber || ''}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Role"
                    value={selectedUserForDocuments.role || ''}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Address"
                    value={selectedUserForDocuments.address || ''}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email"
                    value={selectedUserForDocuments.email || 'Not provided'}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Gender"
                    value={selectedUserForDocuments.gender || 'Not provided'}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="National ID"
                    value={selectedUserForDocuments.nationalID || selectedUserForDocuments.nationalId || 'Not provided'}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Account Verified"
                    value={selectedUserForDocuments.isAccountVerified ? 'Yes' : 'No'}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                  />
                </Grid>
                {isHealthProvider(selectedUserForDocuments.role) && !isPharmacist(selectedUserForDocuments.role) && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Health Provider Details</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="HPCNA Number"
                        value={selectedUserForDocuments.hpcnaNumber || 'Not provided'}
                        fullWidth
                        InputProps={{ readOnly: true }}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="HPCNA Expiry Date"
                        value={selectedUserForDocuments.hpcnaExpiryDate ? new Date(selectedUserForDocuments.hpcnaExpiryDate).toLocaleDateString() : 'Not provided'}
                        fullWidth
                        InputProps={{ readOnly: true }}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Operational Zone"
                        value={selectedUserForDocuments.operationalZone || 'Not provided'}
                        fullWidth
                        InputProps={{ readOnly: true }}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Years of Experience"
                        value={selectedUserForDocuments.yearsOfExperience || 'Not provided'}
                        fullWidth
                        InputProps={{ readOnly: true }}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Document Verification Status"
                        value={selectedUserForDocuments.isDocumentVerified ? 'Verified' : 'Pending'}
                        fullWidth
                        InputProps={{ readOnly: true }}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Documents Submitted"
                        value={selectedUserForDocuments.isDocumentsSubmitted ? 'Yes' : 'No'}
                        fullWidth
                        InputProps={{ readOnly: true }}
                        variant="outlined"
                      />
                    </Grid>
                  </>
                )}
                {isPharmacist(selectedUserForDocuments.role) && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Pharmacy Details</Typography>
                    </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Registered Trading Name"
                            value={selectedUserForDocuments.registeredTradingName || 'Not provided'}
                            fullWidth
                            InputProps={{ readOnly: true }}
                            variant="outlined"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Company Registration No."
                            value={selectedUserForDocuments.companyRegistrationNo || 'Not provided'}
                            fullWidth
                            InputProps={{ readOnly: true }}
                            variant="outlined"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Business Email"
                            value={selectedUserForDocuments.businessEmail || 'Not provided'}
                            fullWidth
                            InputProps={{ readOnly: true }}
                            variant="outlined"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Pharmacy Council No."
                            value={selectedUserForDocuments.pharmacyCouncilNo || 'Not provided'}
                            fullWidth
                            InputProps={{ readOnly: true }}
                            variant="outlined"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Practice Number"
                            value={selectedUserForDocuments.practiceNumber || 'Not provided'}
                            fullWidth
                            InputProps={{ readOnly: true }}
                            variant="outlined"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Settlement Cell Number"
                            value={selectedUserForDocuments.settlementCellNumber || 'Not provided'}
                            fullWidth
                            InputProps={{ readOnly: true }}
                            variant="outlined"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="GPS Coordinates"
                            value={
                              selectedUserForDocuments.gpsCoordinates?.latitude != null &&
                              selectedUserForDocuments.gpsCoordinates?.longitude != null
                                ? `${selectedUserForDocuments.gpsCoordinates.latitude}, ${selectedUserForDocuments.gpsCoordinates.longitude}`
                                : 'Not provided'
                            }
                            fullWidth
                            InputProps={{ readOnly: true }}
                            variant="outlined"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="HPCNA License Expiry Acknowledged"
                            value={selectedUserForDocuments.hpcnaLicenseExpiryAcknowledged ? 'Yes' : 'No'}
                            fullWidth
                            InputProps={{ readOnly: true }}
                            variant="outlined"
                          />
                        </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="HPCNA Number"
                        value={selectedUserForDocuments.hpcnaNumber || 'Not provided'}
                        fullWidth
                        InputProps={{ readOnly: true }}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="HPCNA Expiry Date"
                        value={selectedUserForDocuments.hpcnaExpiryDate ? new Date(selectedUserForDocuments.hpcnaExpiryDate).toLocaleDateString() : 'Not provided'}
                        fullWidth
                        InputProps={{ readOnly: true }}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Document Verification Status"
                        value={selectedUserForDocuments.isDocumentVerified ? 'Verified' : 'Pending'}
                        fullWidth
                        InputProps={{ readOnly: true }}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Documents Submitted"
                        value={selectedUserForDocuments.isDocumentsSubmitted ? 'Yes' : 'No'}
                        fullWidth
                        InputProps={{ readOnly: true }}
                        variant="outlined"
                      />
                    </Grid>
                  </>
                )}
              </Grid>

              {/* Patient Documents Section - show each file independently */}
              {selectedUserForDocuments.role === 'patient' && (
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>Patient Documents</Typography>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                        ID Document Front
                      </Typography>
                      <UploadedFilePreview
                        filename={selectedUserForDocuments.idDocumentFront}
                        label="ID Document Front"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                        ID Document Back
                      </Typography>
                      <UploadedFilePreview
                        filename={selectedUserForDocuments.idDocumentBack}
                        label="ID Document Back"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                        Profile Image
                      </Typography>
                      <UploadedFilePreview
                        filename={selectedUserForDocuments.profileImage}
                        label="Profile Image"
                      />
                    </Box>
                  </Grid>
                </Grid>
              )}

              {/* Documents Section - health providers: show whenever docs exist or were marked submitted */}
              {isHealthProvider(selectedUserForDocuments.role) && (
                selectedUserForDocuments.isDocumentsSubmitted ||
                selectedUserForDocuments.idDocumentFront ||
                selectedUserForDocuments.idDocumentBack ||
                selectedUserForDocuments.finalQualification ||
                selectedUserForDocuments.primaryQualification ||
                selectedUserForDocuments.hpcnaCertificate ||
                selectedUserForDocuments.HPCNAQualification ||
                selectedUserForDocuments.annualQualification
              ) && (
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>Submitted Documents</Typography>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                      ID Document Front
                    </Typography>
                    <UploadedFilePreview
                      filename={selectedUserForDocuments.idDocumentFront}
                      label="ID Document Front"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                      ID Document Back
                    </Typography>
                    <UploadedFilePreview
                      filename={selectedUserForDocuments.idDocumentBack}
                      label="ID Document Back"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                      {isPharmacist(selectedUserForDocuments.role) ? 'Bachelor Certificate' : 'Primary Qualification'}
                    </Typography>
                    <UploadedFilePreview
                      filename={selectedUserForDocuments.finalQualification || selectedUserForDocuments.primaryQualification}
                      label={isPharmacist(selectedUserForDocuments.role) ? 'Bachelor Certificate' : 'Primary Qualification'}
                      asPdf
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                      HPCNA Certificate
                    </Typography>
                    <UploadedFilePreview
                      filename={
                        selectedUserForDocuments.hpcnaCertificate ||
                        selectedUserForDocuments.HPCNAQualification ||
                        selectedUserForDocuments.annualQualification
                      }
                      label="HPCNA Certificate"
                      asPdf
                    />
                  </Box>
                </Grid>
                {selectedUserForDocuments.dispensingCertificateLicence && (
                  <Grid item xs={12} sm={6} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                        Dispensing Certificate / Licence
                      </Typography>
                      <UploadedFilePreview
                        filename={selectedUserForDocuments.dispensingCertificateLicence}
                        label="Dispensing Certificate / Licence"
                        asPdf
                      />
                    </Box>
                  </Grid>
                )}
                {isPharmacist(selectedUserForDocuments.role) && (
                  <>
                    {[
                      { label: 'Training Certificate', field: 'trainingCertificate' },
                      { label: 'NQA Evaluation', field: 'NQAEvaluation' },
                    ].map(({ label, field }) => (
                      selectedUserForDocuments[field] ? (
                        <Grid item xs={12} sm={6} md={6} key={field}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                              {label}
                            </Typography>
                            <UploadedFilePreview
                              filename={selectedUserForDocuments[field]}
                              label={label}
                              asPdf
                            />
                          </Box>
                        </Grid>
                      ) : null
                    ))}
                  </>
                )}
                </Grid>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDocumentsDialogOpen(false);
            setSelectedUserForDocuments(null);
          }}>
            Close
          </Button>
          {selectedUserForDocuments && (
            <>
              {/* Buttons for patients */}
              {selectedUserForDocuments.role === 'patient' &&
               selectedUserForDocuments.idDocumentFront && 
               selectedUserForDocuments.idDocumentBack && 
               selectedUserForDocuments.profileImage && (
                <>
                  {/* Approve button for pending documents */}
                  {!selectedUserForDocuments.isDocumentVerified && canWrite && (
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => {
                        handleApproveDocuments(selectedUserForDocuments.id || selectedUserForDocuments._id);
                        setDocumentsDialogOpen(false);
                        setSelectedUserForDocuments(null);
                      }}
                    >
                      Approve
                    </Button>
                  )}
                  {/* Reject button - always available for patients with documents */}
                  {canWrite && (
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => {
                        setDocumentsDialogOpen(false);
                        openRejectDialog(selectedUserForDocuments.id || selectedUserForDocuments._id);
                        setSelectedUserForDocuments(null);
                      }}
                    >
                      {selectedUserForDocuments.isDocumentVerified ? 'Reject Verification' : 'Reject'}
                    </Button>
                  )}
                </>
              )}
              {/* Buttons for health providers */}
              {isHealthProvider(selectedUserForDocuments.role) &&
               selectedUserForDocuments.isDocumentsSubmitted && (
                <>
                  {/* Approve button for pending documents */}
                  {!selectedUserForDocuments.isDocumentVerified && canWrite && (
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => {
                        handleApproveDocuments(selectedUserForDocuments.id || selectedUserForDocuments._id);
                        setDocumentsDialogOpen(false);
                        setSelectedUserForDocuments(null);
                      }}
                    >
                      Approve
                    </Button>
                  )}
                  {/* Reject button - always available for health providers with documents */}
                  {canWrite && (
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => {
                        setDocumentsDialogOpen(false);
                        openRejectDialog(selectedUserForDocuments.id || selectedUserForDocuments._id);
                        setSelectedUserForDocuments(null);
                      }}
                    >
                      {selectedUserForDocuments.isDocumentVerified ? 'Reject Verification' : 'Reject'}
                    </Button>
                  )}
                </>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </ThemeProvider>
  );
}